import { useEffect, useState } from 'react';

export type Status = 'idle' | 'loading' | 'ready' | 'error';

/**
 * Creates a `<link/>` tag pointing to an external stylesheet that persists. Is NOT cleaned up on unmount.
 */
export default function useExternalStylesheet(href: string): Status {
  const [status, setStatus] = useState<Status>(href ? 'loading' : 'idle');

  useEffect(
    () => {
      // Allow falsy href value if waiting on other data needed for
      // constructing the link URL passed to this hook.
      if (!href) {
        setStatus('idle');
        return;
      }
      // Fetch existing link element by href
      // It may have been added by another intance of this hook
      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      let link: HTMLLinkElement | undefined = document.querySelector(`link[href="${href}"]`);
      if (link == null) {
        // Create link
        link = document.createElement('link');
        link.href = href;
        link.rel = 'stylesheet';
        link.setAttribute('data-status', 'loading');
        // Add link to document head
        document.head.appendChild(link);
      } else {
        // Grab existing link status from attribute and set to state.
        setStatus(link.getAttribute('data-status') as Status);
      }

      link.onload = () => {
        link.setAttribute('data-status', 'ready');
        setStatus('ready');
      };
      link.onerror = () => {
        link.setAttribute('data-status', 'error');
        setStatus('error');
      };
    },
    [href], // Only re-run effect if link href changes
  );

  return status;
}
